/**
 * src/components/cliente/DiagnosticoCliente.jsx
 * Por qué los números de un cliente son los que son.
 *
 * Nace el 04-09 de una observación de Manuel: «la pestaña ejecuciones ya ocupa
 * demasiado contenido que, en parte, no tiene que ver con las ejecuciones
 * puras». Tenía razón y se pudo medir: estas dos tarjetas eran **537 de las 993
 * líneas** de `EjecucionesCliente.jsx` —el 54 %— y estaban en un bloque
 * contiguo, así que el corte no obligó a desenredar nada.
 *
 * La frontera que separa las dos pestañas: **Ejecuciones responde «¿qué está
 * pasando ahora?»; Diagnóstico responde «¿por qué los números son los que
 * son?»**. Y lo de aquí además LANZA procesos que tardan minutos —censar
 * convenios va por tramos, la sonda recorre tiendas—, que no es lo mismo que
 * mirar una cola.
 *
 * Se llama «Diagnóstico» y no «Herramientas» por una razón que no es estética:
 * el módulo Suscripciones YA tiene una pestaña «Diagnóstico» con ese mismo
 * significado y en esa misma posición. Dos palabras distintas para lo mismo en
 * dos módulos es la deuda del §4 aplicada al vocabulario.
 *
 * ⚠️ Esta pestaña necesita el ANCHO COMPLETO, igual que Ejecuciones. Los textos
 * largos de estas dos tarjetas fueron justo el motivo del ensanche del 31-08;
 * si la pestaña nueva hubiera heredado el tope de 960 px de las demás, el
 * traslado habría deshecho aquel arreglo en silencio. Hay guardián.
 */
import { useState } from 'react'
import { Button } from '../ui/Button'
import { NotaAccion } from '../ui/NotaAccion'
import { BloqueJSON } from '../ui/BloqueJSON'
import { Card } from '../ui/Card'
import { getDiagnosticoCatalogo, getCensoOcds, getSondaConvenios } from '../../api/scraping'
import { useFichaCliente } from '../../hooks/useAdminPanel'
import { EstadoDeLaAccion, varianteDeAccion } from './EstadoDeLaAccion'


function CensoConvenios({ clienteId, accion }) {
  const [d, setD] = useState(null)
  const [cargando, setCargando] = useState(false)

  async function pasada() {
    setCargando(true)
    try { setD(await getCensoOcds(clienteId)) }
    catch (e) { setD({ error: e.response?.data?.detail || 'No se pudo censar' }) }
    finally { setCargando(false) }
  }

  const convenios = Array.isArray(d?.convenios) ? d.convenios : []
  const pendientes = d?.ocs_pendientes ?? 0

  // ── ¿Nos cortó el portal? TRES estados, y solo uno es una medida ───────────
  //
  // POR QUÉ ESTÁ AQUÍ (26-09-2026). El tope de censos por noche subió de 1 a 3 el
  // 21-09 y nadie ha medido nunca dónde corta la API de MercadoPúblico. Desde el
  // arreglo del hilo de Datos y scrapers la pasada se PARA en el primer corte y
  // devuelve el número en `esta_pasada`. Hasta hoy ese número vivía solo en la
  // respuesta del endpoint, o sea que leerlo exigía llamarlo a mano — fuera del
  // panel, que es el §8 bis-2 que este mismo censo le reprochaba al tope.
  //
  // 🔑 **Y la trampa es que son tres estados, no dos.** «No cortó» NO significa
  // «medido y aguanta»: significa que esta pasada no llegó al límite. La medida
  // existe **solo** cuando cortó. Pintar los dos casos buenos igual —o pintar un
  // `0`— haría que alguien subiera el tope sobre una medida que no existe, que es
  // el §3 con la forma más cara: un cero que nadie ha medido.
  //
  // ⚠️ Y `sinDato` no es «no cortó»: se distingue a propósito. El campo no existe
  // en las versiones anteriores al arreglo, y dar por «pasada limpia» la ausencia
  // del dato sería exactamente el fallo mudo del §5 — el panel diría «todo bien»
  // porque no sabe preguntarlo. Se lee por `in` y no con `?? false`.
  // ⚠️ `estaPasada` y no `pasada`: ese nombre ya lo usa el manejador del botón, y
  // el build lo rechazó — mejor un rojo de compilación que dos cosas distintas con
  // el mismo nombre en cuarenta líneas.
  const estaPasada = d?.esta_pasada
  const corte = !estaPasada || !('cortado' in estaPasada) ? 'sinDato'
    : estaPasada.cortado ? 'cortado' : 'entera'
  // ── Cuál de los números ES la medida (02-10-2026) ──────────────────────────
  //
  // 🔴 **Este bloque señalaba el número equivocado, y lo decía con énfasis.** Hasta
  // hoy decía de `cortado_tras` que «ese número es la medida que faltaba». El 29-09 le
  // preguntamos a *Datos y scrapers* cuál era —porque `cortado_tras` cuenta órdenes
  // RESUELTAS y no peticiones hechas, y los dos se separan en cuanto algo falla— y la
  // respuesta está escrita en `app/services/censo_ocds.py`:
  //
  //   «El número que se apunta es `consultadas`: peticiones hechas, la que nos
  //    rebotaron incluida, y sumando los dos bucles. […] `cortado_tras` cuántas
  //    resolvió antes — que es contexto y no la medida.»
  //
  // Así que la medida es `consultadas` y `cortado_tras` es contexto. Y encima ese
  // contexto **cambia de unidad**: cuenta órdenes de compra si cortó en la búsqueda de
  // marcos, y convenios si cortó en el repesque de títulos. Por eso no se puede pintar
  // sin `cortado_en` al lado: un número sin unidad no se puede interpretar después,
  // que es exactamente lo que ese fichero explica al negarse a sumar los dos.
  //
  // ⚠️ Y esto llegó tarde a propósito: `cortado_en` entró en `main` el 01-10 y **las 8
  // pruebas de este bloque siguieron en VERDE**, porque no preguntan por una clave que
  // no existía cuando se escribieron. Estaba avisado el 29-09 en tres sitios —el
  // commit, el inventario y el buzón— precisamente porque ningún guardián puede
  // ponerse rojo por un campo que todavía no existe. Se arregla mirando, y se miró.
  const medida = Number.isFinite(estaPasada?.consultadas) ? estaPasada.consultadas : null
  const tras = Number.isFinite(estaPasada?.cortado_tras) ? estaPasada.cortado_tras : null
  const fallidas = Number.isFinite(estaPasada?.fallidas) ? estaPasada.fallidas : null
  // Los dos viajes de una pasada, con la unidad que cuenta cada uno. Las claves son
  // las que escribe el backend; si inventara una tercera, `viaje` queda a null y el
  // texto deja de afirmar una unidad en vez de afirmar la equivocada.
  // ── Las cuentas de la pasada, TAMBIÉN cuando no corta (28-10-2026) ─────────
  //
  // 🔴 POR QUÉ ENTRA. Hasta hoy, si el portal no cortaba, este bloque no enseñaba
  // NI UN NÚMERO: decía «no cortó» y se quedaba ahí. Y «no cortó» es exactamente el
  // caso que justificaría subir `REPONEDOR_TOPE_CENSO`, así que el dato que haría
  // falta para decidir era justo el que no se pintaba. Es el §10 en su forma más
  // barata de arreglar: el número ya viajaba en la respuesta.
  //
  // 📌 Medido, no supuesto. El 28-10-2026 Manuel pulsó el botón sobre `manquehue`
  // por encargo de *Datos y scrapers*, que pedían las cinco cuentas de dos pasadas.
  // Terminó entera y respondió al instante —o sea que apenas pidió nada— y esta caja
  // dijo **lo mismo** que habría dicho tras 150 consultas. Pulsó y no tuvo ni un
  // número que copiar.
  //
  // ⚠️ Y es el §3 por el otro lado: «no cortó tras 3 consultas» y «no cortó tras
  // 150» son dos hechos distintos que esta caja contaba igual. El primero no ha
  // medido nada; el segundo ya es un suelo del límite.
  //
  // 🔑 `consultadas` ausente NO se pinta como 0: se dice que falta. Un 0 inventado
  // aquí se leería como una medida — es la misma razón por la que `reponedor.py` se
  // niega a escribir `.get(clave, 0)` en la línea de log de la noche.
  const cuentas = medida === null
    ? <>Y <b>esta pasada no dice cuántas consultas hizo</b>: <code>consultadas</code>{' '}
        es la medida, así que sin ella esto no se puede interpretar. No se pinta un 0,
        que se leería como una medida que nadie tomó.</>
    : <>Esta pasada hizo <b>{medida} {medida === 1 ? 'consulta' : 'consultas'}</b> al
        portal{fallidas ? <>, de las que <b>{fallidas}</b>{' '}
        {fallidas === 1 ? 'falló' : 'fallaron'}</> : null}. Ese número es lo que
        distingue una pasada que puso a prueba el límite de otra que ni lo rozó.</>

  const VIAJE = {
    marcos:  { donde: 'buscando convenios marco', unidad: 'órdenes' },
    titulos: { donde: 'en el repesque de títulos', unidad: 'convenios' },
  }
  const viaje = VIAJE[estaPasada?.cortado_en] || null
  const etiqueta = cargando ? 'Resolviendo órdenes contra Mercado Público…'
    : d && !d.error && !d.completo ? `Continuar el censo (faltan ${pendientes} órdenes)`
    : d && d.completo ? 'Volver a censar'
    : 'Censar convenios'

  // Opción C (Manuel, 31-08, 2ª ronda), igual que el diagnóstico de arriba.
  return (
    <div className="cm-rejilla-nota">
    <div style={{ minWidth: 0 }}>
      <EstadoDeLaAccion accion={accion} />
      <Button size="sm" variant={varianteDeAccion(accion)} onClick={pasada}
              loading={cargando}>{etiqueta}</Button>

      {d?.error && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                      color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>✗ {d.error}</div>
      )}

      {d && !d.error && (
        <>
          {!d.completo && (
            <div style={{ marginTop: 'var(--space-3)', padding: '10px 12px',
                          borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                          lineHeight: 1.5, color: 'var(--color-warn)',
                          background: 'var(--color-warn-soft)',
                          border: '1px solid var(--color-warn-line)' }}>
              Censo a medias: faltan {pendientes} órdenes por resolver. Las cifras de
              abajo son parciales hasta que no falte ninguna.
            </div>
          )}

          {/* La medida del límite, o la razón por la que todavía no la hay. */}
          <div data-corte={corte} data-consultadas={medida === null ? '' : String(medida)} style={{
            marginTop: 'var(--space-3)', padding: '10px 12px',
            borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
            lineHeight: 1.5, fontFamily: 'var(--font-mono)',
            color: corte === 'cortado' ? 'var(--color-error)' : 'var(--color-text-3)',
            background: corte === 'cortado' ? 'var(--color-error-soft)' : 'transparent',
            border: corte === 'cortado'
              ? '1px solid var(--color-error-line)'
              : '1px solid var(--color-border)',
          }}>
            {corte === 'cortado' && medida !== null && (
              <>🔴 <b>El portal cortó en la consulta nº {medida}.</b>{' '}
                <b>Ese número es la medida</b>: peticiones hechas, contando la que nos
                rebotaron. Hasta hoy nadie sabía dónde corta la API de Mercado Público,
                y por eso el censo atiende solo unos pocos tenants por noche.
                {viaje
                  ? <> Cortó <b>{viaje.donde}</b>{tras !== null
                      ? <>, tras resolver {tras} {viaje.unidad}</>
                      : null}.</>
                  : <> El censo no dice en cuál de sus dos viajes cortó, así que las{' '}
                      {tras} que resolvió antes no se pueden leer: serían órdenes si
                      cortó buscando marcos y convenios si fue en el repesque.</>}
                {fallidas ? <> Otras {fallidas} fallaron por su cuenta, así que lo
                  resuelto y lo consultado no coinciden.</> : null}{' '}
                Pásalo al hilo de Datos y scrapers junto con cuántas órdenes tenía
                pendientes este tenant.</>
            )}
            {corte === 'cortado' && medida === null && (
              <>🔴 <b>El portal cortó la pasada, pero el censo no dice en qué consulta.</b>{' '}
                El corte es el hecho, y <code>consultadas</code> <b>era la medida</b>: se
                ha perdido. Avísale al hilo de Datos y scrapers — con el corte a ciegas
                seguimos sin saber dónde está el límite.{' '}
                {tras !== null
                  ? <>Lo único que llega es que resolvió {tras} antes de cortar, y eso
                      <b> no es el límite</b>: cuenta lo que salió bien, no las
                      peticiones que aguantó el portal.</>
                  : null}</>
            )}
            {corte === 'entera' && (
              <>El portal <b>no cortó</b>: la pasada terminó entera.{' '}
                <b>Eso no es el límite medido</b> — significa que esta pasada no llegó
                a tocarlo. La medida solo existe el día que corte. {cuentas}</>
            )}
            {corte === 'sinDato' && (
              <>No se sabe si el portal cortó: esta versión del censo todavía no lo
                informa. <b>No es lo mismo que «no cortó»</b>, y por eso se dice
                aparte. {cuentas}</>
            )}
          </div>

          {convenios.length > 0 && (
            <table style={{ marginTop: 'var(--space-3)', borderCollapse: 'collapse',
                            fontSize: 'var(--text-sm)', width: '100%' }}>
              <thead>
                <tr style={{ color: 'var(--color-text-3)', textAlign: 'left' }}>
                  {['Convenio', 'Nombre', 'OCs', 'Dinero', '% del total', 'Tienda', 'Soporte'].map(h => (
                    <th key={h} style={{ padding: '6px 10px', fontWeight: 500,
                                         fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                                         borderBottom: '1px solid var(--color-border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {convenios.map(c => (
                  <tr key={c.convenio} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{c.convenio}</td>
                    {/* El nombre lo trae la MISMA respuesta OCDS que resuelve el
                        convenio; se estaba tirando. Sin él la tabla es una lista
                        de códigos que no dice en qué mercados está el tenant. */}
                    <td style={{ padding: '6px 10px', color: c.titulo ? 'var(--color-text)' : 'var(--color-text-3)' }}>
                      {c.titulo || '—'}
                    </td>
                    <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{c.ocs}</td>
                    <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                      ${(c.monto ?? 0).toLocaleString('es-CL')}
                    </td>
                    <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                      {c.pct_del_propio != null ? `${(c.pct_del_propio * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td style={{ padding: '6px 10px' }}>{c.tienda || '—'}</td>
                    <td style={{ padding: '6px 10px' }}>{c.soporte}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <details style={{ marginTop: 'var(--space-3)' }}>
            <summary style={{ cursor: 'pointer', fontSize: 'var(--text-sm)',
                              color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)' }}>
              Ver JSON crudo
            </summary>
            <BloqueJSON datos={d} nombre="censo-convenios" />
          </details>
        </>
      )}
    </div>

    <aside className="cm-nota-lateral" style={{ minWidth: 0 }}>
      <NotaAccion
        que="Averigua en qué convenios marco factura este tenant y cuánto en cada uno, preguntándolo orden a orden a la API pública de Mercado Público. Es lo que contesta «¿qué parte de su negocio vigilamos?» — y esa cifra viaja al panel del cliente y a su correo del viernes."
        coste="varios minutos la primera vez (una petición por orden). Va por tramos: cachea lo resuelto, así que si vuelve diciendo «faltan N órdenes» hay que pulsar otra vez hasta que no falte ninguna."
        mirar="Que no queden órdenes pendientes: con el censo a medias las cifras son parciales y dirían que cubrimos MENOS de lo real. En «Soporte», `sin_mapear` no es dinero perdido — es dinero que aún no se ha comprobado, y para eso está el botón de abajo."
      />
      {d && !d.error && d.lectura && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)',
                      color: 'var(--color-text-3)', lineHeight: 1.5 }}>
          {d.lectura}
        </div>
      )}
    </aside>
    </div>
  )
}



// ── ¿Dónde vive cada convenio sin mapear? ────────────────────────────────────
// El censo deja un cubo `sin_mapear` que no es dinero cubierto ni perdido: es
// dinero del que nadie ha preguntado. Esto pregunta, buscando códigos CM de las
// propias órdenes del tenant en las 12 tiendas conocidas.
//
// ⚠️ La columna que importa NO es «tienda»: es el veredicto. Un `no_concluyente`
// significa que no se pudo mirar (la ficha no trae el JSON que sabemos leer, o la
// tienda no respondió), y pintarlo igual que un «no está» convertiría un «no lo
// sabemos» en un «no se puede». Por eso van con colores distintos.
const _TONO_VEREDICTO = {
  tienda_encontrada: 'var(--color-ok)',
  // Sabemos la tienda, pero no sabemos leerla: no es un hallazgo verde ni un
  // callejón. Es una decisión de producto con coste.
  tienda_identificada_motor_no_la_lee: 'var(--color-running)',
  ninguna_tienda_conocida_lo_tiene: 'var(--color-text-2)',
}

function SondaConvenios({ clienteId, accion }) {
  const [d, setD] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [convenio, setConvenio] = useState('')

  // ⚠️ El endpoint acepta una lista explícita de convenios DESDE SIEMPRE, y con
  // ella se salta su propio filtro: sin lista sondea solo los `sin_mapear`, con
  // lista sondea lo que se le nombre. `getSondaConvenios` ya pasaba el parámetro;
  // lo que faltaba era el control en pantalla.
  //
  // Y no es un extra: `2239-8-LR24` —$404.345.697 de manquehue— está marcado
  // `no_soportado`, no `sin_mapear`, así que el botón normal NO lo mira. Sin este
  // campo, el único instrumento capaz de decir dónde vive ese dinero era
  // inalcanzable desde el panel, y la alternativa era pedirle a Manuel que
  // llamara a una API a mano. Cuando para hacer algo hay que salirse del panel,
  // el trabajo es arreglar el panel.
  async function sondear(rehacer = false) {
    setCargando(true)
    try { setD(await getSondaConvenios(clienteId, { rehacer, convenios: convenio.trim() })) }
    catch (e) { setD({ error: e.response?.data?.detail || 'No se pudo sondear' }) }
    finally { setCargando(false) }
  }

  const filas = Array.isArray(d?.sondeados) ? d.sondeados : []
  const sinCodigos = Array.isArray(d?.sin_codigos_para_sondear) ? d.sin_codigos_para_sondear : []
  const pospuestos = Array.isArray(d?.pospuestos) ? d.pospuestos : []

  return (
    <div className="cm-rejilla-nota"
         style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-4)',
                  borderTop: '1px solid var(--color-border)' }}>
    <div style={{ minWidth: 0 }}>
      <EstadoDeLaAccion accion={accion} />
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center',
                    flexWrap: 'wrap' }}>
        <Button size="sm" variant={varianteDeAccion(accion)} onClick={() => sondear(false)}
                loading={cargando}>
          {cargando ? 'Buscando sus códigos en las tiendas…' : 'Sondear convenios sin tienda conocida'}
        </Button>
        {/* Cada pulsación normal AVANZA a convenios nuevos. Sin esta segunda,
            una respuesta vieja no se podría refrescar nunca.
            ⚠️ `secondary` y no `ghost`: Manuel no la encontraba, y con razón —
            `ghost` es fondo y borde transparentes junto a un botón relleno, o
            sea que se lee como texto suelto, no como algo que se pueda pulsar.
            Un botón que no parece un botón no existe. */}
        <Button size="sm" variant="secondary" onClick={() => sondear(true)} disabled={cargando}>
          Volver a sondear desde cero
        </Button>
      </div>

      {/* El campo va debajo de los botones y NO deshabilita ninguno: vacío es el
          comportamiento de siempre. Es un «además», no un «antes de». */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                    marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
        <label htmlFor="sonda-convenio"
               style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-2)' }}>
          Convenio concreto
        </label>
        <input
          id="sonda-convenio"
          value={convenio}
          onChange={e => setConvenio(e.target.value)}
          placeholder="2239-8-LR24"
          style={{
            padding: '6px 10px', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)', background: 'transparent',
            color: 'var(--color-text)', fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-sm)', width: '170px',
          }}
        />
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-3)' }}>
          opcional · lo sondea aunque ya esté marcado como «no soportado»,
          que es lo que el botón de al lado NO hace
        </span>
      </div>

      {(d?.error || d?.motivo) && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                      color: d.error ? 'var(--color-error)' : 'var(--color-text-3)',
                      fontFamily: 'var(--font-mono)' }}>
          {d.error ? `✗ ${d.error}` : d.motivo}
        </div>
      )}

      {filas.length > 0 && (
        <table style={{ marginTop: 'var(--space-3)', borderCollapse: 'collapse',
                        fontSize: 'var(--text-sm)', width: '100%' }}>
          <thead>
            <tr style={{ color: 'var(--color-text-3)', textAlign: 'left' }}>
              {['Convenio', 'Nombre', 'OCs', 'Dinero', 'Tienda', 'Veredicto'].map(h => (
                <th key={h} style={{ padding: '6px 10px', fontWeight: 500,
                                     fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                                     borderBottom: '1px solid var(--color-border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.map(f => (
              <tr key={f.convenio} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{f.convenio}</td>
                <td style={{ padding: '6px 10px', color: f.titulo ? 'var(--color-text)' : 'var(--color-text-3)' }}>
                  {f.titulo || '—'}
                </td>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{f.ocs ?? '—'}</td>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                  ${(f.monto ?? 0).toLocaleString('es-CL')}
                </td>
                <td style={{ padding: '6px 10px' }}>
                  {f.tienda || (f.tienda_probable
                    ? <span style={{ color: 'var(--color-running)' }}
                            title="La tienda tiene el código en su ficha, pero nuestro motor no sabe leerla">
                        {f.tienda_probable} (sin leer)
                      </span>
                    : '—')}
                </td>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)',
                             fontSize: 'var(--text-xs)',
                             color: _TONO_VEREDICTO[f.veredicto] || 'var(--color-warn)' }}>
                  {f.veredicto}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {sinCodigos.length > 0 && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                      color: 'var(--color-text-2)', lineHeight: 1.5 }}>
          Sin códigos CM en sus líneas, así que no hay con qué llamar a la puerta de
          ninguna tienda:{' '}
          {sinCodigos.map(c => `${c.convenio} ($${(c.monto ?? 0).toLocaleString('es-CL')})`).join(', ')}.
        </div>
      )}

      {pospuestos.length > 0 && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)',
                      color: 'var(--color-text-3)', lineHeight: 1.5 }}>
          Quedan {pospuestos.length} convenios por mirar (van de más caro a menos).
          Vuelve a pulsar «Sondear convenios sin tienda conocida»: cada pulsación
          avanza a los siguientes y lo ya encontrado se conserva.
        </div>
      )}

      {/* ⚠️ El detalle por tienda NO es opcional aquí. Un `no_concluyente` dice
          que no se pudo mirar, pero no CUÁL tienda dejó el hueco — y eso es
          justo lo accionable: si la ficha ilegible la devolvió la tienda que ya
          sabemos rica (aseo3), el convenio probablemente vive ahí y lo que falla
          es nuestro parser; si la devolvió otra, no dice nada. El veredicto sin
          su evidencia obliga a creerme, que es lo que este panel evita. */}
      {filas.length > 0 && (
        <details style={{ marginTop: 'var(--space-3)' }}>
          <summary style={{ cursor: 'pointer', fontSize: 'var(--text-sm)',
                            color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)' }}>
            Ver qué contestó cada tienda
          </summary>
          <BloqueJSON datos={d} nombre="convenios-por-tienda" />
        </details>
      )}
    </div>

    <aside className="cm-nota-lateral" style={{ minWidth: 0 }}>
      <NotaAccion
        que="Coge códigos de las propias órdenes del tenant en los convenios de los que NO sabemos en qué tienda viven y los busca en las 12 tiendas conocidas, hasta dar con la que los sirve. Convierte «no sabemos dónde está este dinero» en una respuesta."
        coste="varios minutos en el peor caso; para en cuanto una tienda confirma. NO escribe en la tabla de licitaciones: adoptar un resultado es un acto aparte."
        mirar="`tienda_encontrada` es el único resultado cerrado. `tienda_identificada_motor_no_la_lee` significa que la tienda SÍ tiene el código y el problema es nuestro. Y un `no_concluyente` no dice que el convenio no exista: dice que no se pudo comprobar. Si quedan convenios por mirar, se dice al lado: vuelve a pulsar hasta que no queden."
      />
      {d?.lectura && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)',
                      color: 'var(--color-text-3)', lineHeight: 1.5 }}>
          {d.lectura}
        </div>
      )}
    </aside>
    </div>
  )
}

// ── Reparto del Convenio Marco por destino del código ────────────────────────
// El bloque que contesta la pregunta de Manuel: «¿cuánto de ese dinero fuera del
// catálogo podemos recuperar?». Cada destino tiene una palanca DISTINTA, y esa
// es toda la utilidad: sin el desglose, «1.044 millones fuera» es una cifra que
// solo produce inquietud.
//
// ⚠️ 04-09, y es un defecto mío repetido: los seis destinos existen en el
// backend desde el 03-09, pero solo dentro de «Ver JSON crudo» — y le pedí a
// Manuel «el bloque reparto» de un panel que nunca lo pintó. Es exactamente lo
// que ya avisaba el comentario de `PorQueNoClasifica` doce líneas más abajo:
// una medida que hay que ir a buscar no está entregada.
const DESTINO_CM = {
  en_catalogo: ['En el catálogo', 'se analiza y se ve en el panel'],
  descartado_por_la_tienda: ['La tienda no encuentra el código',
    'es de otro convenio: solo se recupera si ese convenio está soportado y activo'],
  medido_sin_ofertas: ['Sondeado y sin oferentes',
    'sí se miró y el portal no dio ni una oferta — repetir el run de precios, NO regenerar el catálogo'],
  no_verificado: ['El portal no dejó comprobarlo',
    '404 intermitente: se reintenta solo en el próximo run'],
  sin_ficha_en_el_portal: ['El portal lo indexa y no publica su ficha',
    'lleva varios runs igual: no hay precios que vigilar ahí y ningún run lo arregla — se re-prueba de tanto en tanto por si el portal lo repone'],
  no_probado: ['Nunca se miró',
    'el catálogo va por detrás de las OCs, o el código quedó fuera del tope — regenerar el catálogo'],
  codigo_no_es_cm: ['El código no es de Convenio Marco',
    'no son 7 dígitos (suele ser un UNSPSC): nunca iba a casar, y no es un hueco de cobertura'],
  sin_codigo: ['La línea no trae código',
    'la orden no dice qué producto es: no hay nada que cruzar'],
}

function RepartoConvenioMarco({ datos }) {
  if (!datos || datos.error) return null
  if (!datos.medible) {
    return (
      <div style={{ marginTop: '14px', fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-3)', fontFamily: 'var(--font-ui)' }}>
        Reparto del Convenio Marco: no se pudo medir — {datos.motivo}
        {datos.lectura && (
          <div style={{ fontSize: 'var(--text-xs)', marginTop: 'var(--space-1)' }}>{datos.lectura}</div>
        )}
      </div>
    )
  }
  const filas = Object.entries(datos.reparto || {})
  if (!filas.length) return null
  const total = datos.monto_cm_en_lineas || 0
  // Aritmética de presentación sobre lo que ya está en pantalla, no una medida
  // nueva: todo lo que no acaba en el catálogo.
  const fuera = filas.reduce((a, [k, v]) => a + (k === 'en_catalogo' ? 0 : (v.monto || 0)), 0)
  // ⚠️ El nivel lo dice el backend (`veredicto_nivel`), que es quien escribe el
  // veredicto. Antes se deducía aquí leyendo el texto —`startsWith('cobertura_sana')`—,
  // o sea el mismo hecho decidido en dos sitios (§4 de ERRORES_RECURRENTES):
  // bastaba con reescribir la frase del veredicto para que el color dejara de
  // corresponderse con lo que dice.
  // El `startsWith` se conserva SOLO como respaldo para una respuesta vieja que
  // todavía no traiga el campo; no es el camino normal.
  const nivel = datos.veredicto_nivel
    || (String(datos.veredicto || '').startsWith('cobertura_sana') ? 'sano' : 'alerta')
  const alerta = nivel === 'alerta'
  // «No concluyente» no es una alarma ni un aprobado: es que no hay bastante
  // dinero para juzgar. Pintarlo de ámbar diría que algo va mal; pintarlo como
  // sano diría que va bien. Ninguna de las dos es cierta.
  const neutro = nivel === 'no_concluyente'

  return (
    <div style={{ marginTop: '18px' }}>
      <div style={{ fontSize: 'var(--text-xs)', letterSpacing: '0.08em',
                    color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)',
                    marginBottom: '6px' }}>
        A DÓNDE VA EL DINERO DE CONVENIO MARCO
      </div>
      <table style={{ borderCollapse: 'collapse', fontSize: 'var(--text-sm)', width: '100%' }}>
        <thead>
          <tr style={{ color: 'var(--color-text-3)', textAlign: 'left' }}>
            {['Destino del código', 'Dinero', '%', 'Códigos'].map(h => (
              <th key={h} style={{ padding: '6px 10px', fontWeight: 500,
                                   fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                                   borderBottom: '1px solid var(--color-border)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map(([k, v]) => {
            const [nombre, palanca] = DESTINO_CM[k] || [k, '']
            return (
              <tr key={k} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '6px 10px' }}>
                  <div style={{ color: 'var(--color-text-2)' }}>{nombre}</div>
                  {palanca && (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>
                      {palanca}
                    </div>
                  )}
                </td>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                  ${(v.monto ?? 0).toLocaleString('es-CL')}
                </td>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                  {/* `null` es «no hay dinero que repartir», que NO es un 0 % */}
                  {v.pct != null ? `${(v.pct * 100).toFixed(1)}%` : '—'}
                </td>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                  {(v.codigos ?? 0).toLocaleString('es-CL')}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {/* ⚠️ Etiqueta distinta a propósito. El panel de familias tiene un «fuera
          del catálogo medido» que cuenta TODAS las órdenes; esto cuenta solo las
          líneas de Convenio Marco. Dos universos con el mismo rótulo ya costaron
          un contador duplicado el 04-09: no se repite el rótulo. */}
      <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-ui)',
                    color: 'var(--color-text-2)' }}>
        No llega al catálogo: <strong style={{ fontFamily: 'var(--font-mono)' }}>
          ${fuera.toLocaleString('es-CL')}
        </strong> de ${total.toLocaleString('es-CL')} en líneas de Convenio Marco.
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>
          No es la misma cifra que «fuera del catálogo medido» del bloque de familias:
          aquella cuenta todas las órdenes del tenant, esta solo las de Convenio Marco.
        </div>
      </div>
      <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-sm)', lineHeight: 1.5, fontFamily: 'var(--font-ui)',
                    color: alerta ? 'var(--color-warn)' : 'var(--color-text-2)',
                    background: alerta ? 'var(--color-warn-soft)' : 'var(--color-surface-2)',
                    border: `1px solid ${alerta ? 'var(--color-warn-line)' : 'var(--color-border)'}`,
                    // Un filete gris a la izquierda separa «no concluyente» de
                    // «sano»: los dos van en neutro —ninguno es una alarma— pero no
                    // dicen lo mismo, y sin marca alguna el que no se ha podido
                    // juzgar se lee como aprobado.
                    borderLeft: neutro ? '3px solid var(--color-text-3)' : undefined }}>
        {datos.veredicto}
      </div>
      {datos.tiendas_activas_no_soportadas?.length > 0 && (
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)',
                      color: 'var(--color-warn)' }}>
          Tiendas activas que el producto no sabe leer: {datos.tiendas_activas_no_soportadas.join(', ')}
        </div>
      )}
    </div>
  )
}


// ── Cobertura por antigüedad de la orden ─────────────────────────────────────
// Contesta si el hueco de cobertura es producto que la tienda ya no publica (y
// entonces la palanca es acortar la ventana de medición) o un catálogo mal
// construido (y entonces es un fallo que hay que cazar). El veredicto lo calcula
// el backend; aquí solo se pinta, para que no haya dos criterios.
function CoberturaPorAntiguedad({ datos }) {
  if (!datos) return null
  if (!datos.medible) {
    return (
      <div style={{ marginTop: '14px', fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-3)', fontFamily: 'var(--font-ui)' }}>
        Cobertura por antigüedad: no se pudo medir — {datos.motivo}
      </div>
    )
  }
  const tramos = Array.isArray(datos.tramos) ? datos.tramos : []
  const alerta = String(datos.veredicto || '').startsWith('la_antiguedad_no_lo_explica')

  return (
    <div style={{ marginTop: '18px' }}>
      <div style={{ fontSize: 'var(--text-xs)', letterSpacing: '0.08em',
                    color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)',
                    marginBottom: '6px' }}>
        COBERTURA POR ANTIGÜEDAD DE LA ORDEN
      </div>
      <table style={{ borderCollapse: 'collapse', fontSize: 'var(--text-sm)', width: '100%' }}>
        <thead>
          <tr style={{ color: 'var(--color-text-3)', textAlign: 'left' }}>
            {['Antigüedad', 'Dinero CM', 'En catálogo', 'Descartado', 'Sin probar', '% cubierto'].map(h => (
              <th key={h} style={{ padding: '6px 10px', fontWeight: 500,
                                   fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                                   borderBottom: '1px solid var(--color-border)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tramos.map(t => (
            <tr key={t.etiqueta} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '6px 10px' }}>{t.etiqueta}</td>
              {[t.monto_cm, t.en_catalogo, t.descartado, t.no_probado].map((v, i) => (
                <td key={i} style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                  ${(v ?? 0).toLocaleString('es-CL')}
                </td>
              ))}
              <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>
                {/* `null` es "tramo sin dinero", que NO es un 0 % de cobertura */}
                {t.pct_en_catalogo != null ? `${(t.pct_en_catalogo * 100).toFixed(1)}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-sm)', lineHeight: 1.5, fontFamily: 'var(--font-ui)',
                    color: alerta ? 'var(--color-warn)' : 'var(--color-text-2)',
                    background: alerta ? 'var(--color-warn-soft)' : 'var(--color-surface-2)',
                    border: `1px solid ${alerta ? 'var(--color-warn-line)' : 'var(--color-border)'}` }}>
        {datos.veredicto}
      </div>
      {datos.ocs_sin_fecha_legible > 0 && (
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)',
                      color: 'var(--color-text-3)' }}>
          {datos.ocs_sin_fecha_legible} órdenes sin fecha legible (${(datos.monto_sin_fecha_legible ?? 0).toLocaleString('es-CL')}),
          contadas aparte y no repartidas entre los tramos.
        </div>
      )}
    </div>
  )
}


// ⚠️ 02-09: este bloque nace de haber puesto la medida donde su lector no la
// mira. Añadí `por_que_no_clasifica` al JSON del endpoint y le dije a Manuel que
// abriera el embudo; el embudo la tenía, pero dentro de «Ver JSON crudo». La
// respuesta estaba a un clic de distancia y aun así no existía para quien tenía
// que leerla — el mismo defecto del runbook que hubo que reescribir en llano.
// Una medida que hay que ir a buscar no está entregada.
function PorQueNoClasifica({ datos }) {
  if (!datos || datos.error) return null
  const n = datos.resultados || 0
  if (!n) return null

  // El veredicto se calcula aquí porque es aritmética de presentación —qué fila
  // manda—, no criterio: el criterio (qué significa cada una) vive en `lectura`,
  // en el backend, y hay uno solo.
  const filas = [
    ['El sondeo falló', datos.fallo_el_sondeo,
     'no se pudo leer el portal — es fallo nuestro'],
    ['Se leyó y no hay oferentes', datos.sondeado_sin_ofertas,
     'la tienda no publica oferentes: no hay nada que repartir'],
    ['Con ofertas', datos.con_ofertas, 'lo que sí llega al panel'],
  ]
  const manda = filas.filter(f => f[0] !== 'Con ofertas')
    .sort((a, b) => (b[1] || 0) - (a[1] || 0))[0]
  const alerta = (manda?.[1] || 0) / n > 0.5

  const url = [
    ['Sin URL', datos.sin_url],
    ['URL del buscador', datos.url_buscador],
    ['Categoría fuera de la taxonomía', datos.slug_fuera_de_taxonomia],
    ['Categoría en la taxonomía', datos.slug_en_taxonomia],
  ]

  return (
    <div style={{ marginTop: '18px' }}>
      <div style={{ fontSize: 'var(--text-xs)', letterSpacing: '0.08em',
                    color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)',
                    marginBottom: '6px' }}>
        POR QUÉ NO CLASIFICA
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
        <div style={{ minWidth: '260px', flex: '1 1 260px' }}>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                        fontFamily: 'var(--font-ui)', marginBottom: 'var(--space-1)' }}>
            De los {n.toLocaleString('es-CL')} productos sondeados
          </div>
          {filas.map(([k, v, por_que]) => (
            <div key={k} style={{ padding: '6px 0',
                                  borderBottom: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                            gap: 'var(--space-4)', fontSize: 'var(--text-base)' }}>
                <span style={{ color: 'var(--color-text-2)' }}>{k}</span>
                <span style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                  {(v ?? 0).toLocaleString('es-CL')}
                </span>
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)' }}>
                {por_que}
              </div>
            </div>
          ))}
        </div>
        <div style={{ minWidth: '260px', flex: '1 1 260px' }}>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
                        fontFamily: 'var(--font-ui)', marginBottom: 'var(--space-1)' }}>
            De los {(datos.en_analisis ?? 0).toLocaleString('es-CL')} que llegan al
            panel, de dónde sale su familia
          </div>
          {url.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between',
                                  gap: 'var(--space-4)', padding: '6px 0',
                                  borderBottom: '1px solid var(--color-border)',
                                  fontSize: 'var(--text-base)' }}>
              <span style={{ color: 'var(--color-text-2)' }}>{k}</span>
              <span style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                {(v ?? 0).toLocaleString('es-CL')}
              </span>
            </div>
          ))}
        </div>
      </div>
      {alerta && (
        <div style={{ marginTop: '10px', padding: '10px 12px',
                      borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                      lineHeight: 1.5, fontFamily: 'var(--font-ui)',
                      color: 'var(--color-warn)', background: 'var(--color-warn-soft)',
                      border: '1px solid var(--color-warn-line)' }}>
          Manda «{manda[0].toLowerCase()}»: {manda[2]}. Mientras eso no cambie, ni
          la taxonomía ni el tope de catálogo van a mover el reparto por familias.
        </div>
      )}
      {datos.taxonomia_cargada != null && (
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)',
                      fontFamily: 'var(--font-mono)',
                      color: datos.taxonomia_cargada ? 'var(--color-text-3)' : 'var(--color-warn)' }}>
          Taxonomía cargada: {datos.taxonomia_cargada.toLocaleString('es-CL')} entradas
          {datos.tiendas?.length ? ` · tiendas del tenant: ${datos.tiendas.join(', ')}` : ' · sin tiendas resueltas'}
          {!datos.taxonomia_cargada && (
            <div style={{ fontFamily: 'var(--font-ui)', marginTop: 'var(--space-05)' }}>
              Con la taxonomía vacía, TODO cae en «fuera de la taxonomía» aunque el
              archivo de la tienda esté bien: lo que falla es la tienda del tenant,
              no la clasificación.
            </div>
          )}
        </div>
      )}
      {datos.top_slugs?.length > 0 && (
        <details style={{ marginTop: 'var(--space-2)' }}>
          <summary style={{ cursor: 'pointer', fontSize: 'var(--text-sm)',
                            color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)' }}>
            Ver las categorías más frecuentes ({datos.top_slugs.length})
          </summary>
          <div style={{ marginTop: 'var(--space-2)', fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--text-xs)', color: 'var(--color-text-2)' }}>
            {datos.top_slugs.map(s => (
              <div key={s.slug}>
                {String(s.n).padStart(4, ' ')} · {s.slug} → {s.familia || 'SIN FAMILIA'}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}


function DiagnosticoCatalogo({ clienteId, accion }) {
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(false)

  async function run() {
    setLoading(true); setD(null)
    try { setD(await getDiagnosticoCatalogo(clienteId)) }
    catch (e) { setD({ error: e.response?.data?.detail || 'No se pudo leer el diagnóstico' }) }
    finally { setLoading(false) }
  }

  const Group = ({ titulo, filas }) => (
    <div style={{ minWidth: '220px', flex: '1 1 220px' }}>
      <div style={{ fontSize: 'var(--text-xs)', letterSpacing: '0.08em', color: 'var(--color-text-3)',
                    fontFamily: 'var(--font-mono)', marginBottom: '6px' }}>{titulo}</div>
      {filas.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)',
                              padding: '6px 0', borderBottom: '1px solid var(--color-border)', fontSize: 'var(--text-base)' }}>
          <span style={{ color: 'var(--color-text-2)' }}>{k}</span>
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text)', whiteSpace: 'nowrap' }}>{v}</span>
        </div>
      ))}
    </div>
  )

  // Opción C (Manuel, 31-08, 2ª ronda): los datos a la izquierda a todo lo
  // ancho y la explicación en la columna estrecha de la derecha. Estaba encima
  // de la tabla, ocupando 147 px de alto y 546 px de los 958 de la tarjeta, y
  // empujaba hacia abajo justo lo que se venía a mirar. Es el mismo reparto que
  // ya eligió para la Sonda de tiendas.
  return (
    <div className="cm-rejilla-nota">
    <div style={{ minWidth: 0 }}>
      <EstadoDeLaAccion accion={accion} />
      <Button size="sm" variant={varianteDeAccion(accion)} onClick={run}
              loading={loading}>Ver embudo OCs → catálogo</Button>
      {d?.error && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-error)',
                      fontFamily: 'var(--font-mono)' }}>✗ {d.error}</div>
      )}
      {d && !d.error && (
        <>
          <div style={{ marginTop: '14px', display: 'flex', flexWrap: 'wrap', gap: '28px' }}>
            <Group titulo="OCS_COMPLETO" filas={[
              ['OCs totales', d.ocs_completo.total],
              ['OCs propias', d.ocs_completo.propias],
            ]} />
            <Group titulo="OCS_DETALLE" filas={[
              ['OCs con detalle', `${d.ocs_detalle.ocs_con_productos}/${d.ocs_detalle.ocs}`],
              ['Líneas', d.ocs_detalle.lineas],
              ['Códigos CM distintos', d.ocs_detalle.codigos_cm_distintos],
            ]} />
            <Group titulo="CATÁLOGO" filas={[
              ['Códigos de OCs', d.catalogo.total_codigos_oc ?? '—'],
              ['En catálogo', d.catalogo.en_catalogo],
              ['Descartados', d.catalogo.descartados],
            ]} />
          </div>
          {/* A dónde va cada peso de CM, por destino del código. Primero esto y
              después la antigüedad: el reparto dice CUÁNTO se queda fuera y con
              qué palanca; la antigüedad, si el hueco es de órdenes viejas. */}
          <RepartoConvenioMarco datos={d.cobertura_tienda?.reparto} />

          {/* Cobertura por antigüedad: ¿el dinero descartado es de OCs viejas? Estaba
              solo en el JSON crudo, que no es sitio donde nadie lea un veredicto. */}
          <CoberturaPorAntiguedad datos={d.cobertura_tienda?.por_antiguedad} />

          {/* Y por qué el reparto por familias dice lo que dice (02-09). */}
          <PorQueNoClasifica datos={d.por_que_no_clasifica} />

          {/* JSON crudo: garantiza legibilidad completa aunque el resumen quede estrecho */}
          <details style={{ marginTop: 'var(--space-3)' }}>
            <summary style={{ cursor: 'pointer', fontSize: 'var(--text-sm)', color: 'var(--color-text-2)',
                              fontFamily: 'var(--font-mono)' }}>Ver JSON crudo</summary>
            <BloqueJSON datos={d} nombre="diagnostico-tenant" />
          </details>
        </>
      )}
    </div>

    <aside className="cm-nota-lateral" style={{ minWidth: 0 }}>
      <NotaAccion
        que="Reparte todo el dinero de las órdenes del tenant y enseña qué parte acaba dentro de su catálogo y qué parte se queda fuera, con el motivo. Es el diagnóstico que dice si un tenant va a ver un panel útil o medio vacío."
        coste="unos segundos. Lee lo que ya está en base de datos: no pide nada al portal ni cambia nada."
        mirar="El porcentaje sobre dinero de Convenio Marco: por debajo del 60 % el tenant necesita un aviso de cobertura. Y el bloque por antigüedad dice si el hueco es catálogo viejo o catálogo mal construido, que se arreglan de forma distinta."
      />
      {d && !d.error && d.lectura && (
        <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)',
                      color: 'var(--color-text-3)', fontFamily: 'var(--font-ui)',
                      lineHeight: 1.5 }}>
          {d.lectura}
        </div>
      )}
    </aside>
    </div>
  )
}


export function DiagnosticoCliente({ clienteId }) {
  // La ficha se pide UNA vez aquí y se reparte, en vez de una llamada por
  // tarjeta: es la misma consulta y así las tres tarjetas no pueden enseñar
  // estados de momentos distintos del mismo tenant.
  const { data: ficha } = useFichaCliente(clienteId)
  const acciones = Array.isArray(ficha?.acciones) ? ficha.acciones : []
  const accion = (clave) => acciones.find(a => a.clave === clave) || null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <Card
        title="Diagnóstico del catálogo"
        subtitle="Por qué el catálogo tiene los códigos que tiene (OCs → filtro tienda)"
        style={{ flexShrink: 0 }}
      >
        <DiagnosticoCatalogo clienteId={clienteId} accion={accion('embudo')} />
      </Card>

      <Card
        title="Convenios en los que factura"
        subtitle="Resuelto orden a orden contra Mercado Público. Va por tramos: puede necesitar varias pulsaciones"
        style={{ flexShrink: 0 }}
      >
        <CensoConvenios clienteId={clienteId} accion={accion('censo')} />
        <SondaConvenios clienteId={clienteId} accion={accion('sonda')} />
      </Card>
    </div>
  )
}
